import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { PropertyKitCategory, CategoryFormData } from './types';

const QUERY_KEY = ['property-kit-categories'];

export function usePropertyKitCategories() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all categories
  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<PropertyKitCategory[]> => {
      const { data, error } = await supabase
        .from('property_kit_categories')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });

      if (error) throw error;
      return data as PropertyKitCategory[];
    },
  });

  // Create category
  const createMutation = useMutation({
    mutationFn: async (input: CategoryFormData) => {
      // Get max sort_order to auto-assign next value
      const { data: existing } = await supabase
        .from('property_kit_categories')
        .select('sort_order')
        .order('sort_order', { ascending: false })
        .limit(1);

      const maxSortOrder = existing?.[0]?.sort_order ?? -1;

      const { data, error } = await supabase
        .from('property_kit_categories')
        .insert({
          name: input.name,
          sort_order: input.sort_order ?? maxSortOrder + 1,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Categoría creada',
        description: 'La categoría se ha creado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo crear la categoría: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Update category
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...input }: CategoryFormData & { id: string }) => {
      const { data, error } = await supabase
        .from('property_kit_categories')
        .update({
          name: input.name,
          sort_order: input.sort_order,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Categoría actualizada',
        description: 'La categoría se ha actualizado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo actualizar la categoría: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Delete category
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('property_kit_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast({
        title: 'Categoría eliminada',
        description: 'La categoría se ha eliminado correctamente.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `No se pudo eliminar la categoría. Verifica que no tenga artículos asociados.`,
        variant: 'destructive',
      });
    },
  });

  // Reorder categories with optimistic update
  const reorderMutation = useMutation({
    mutationFn: async ({
      categoryId,
      newSortOrder,
      swapCategoryId,
      swapSortOrder,
    }: {
      categoryId: string;
      newSortOrder: number;
      swapCategoryId: string;
      swapSortOrder: number;
    }) => {
      // Execute both updates in parallel
      const [result1, result2] = await Promise.all([
        supabase
          .from('property_kit_categories')
          .update({ sort_order: newSortOrder })
          .eq('id', categoryId),
        supabase
          .from('property_kit_categories')
          .update({ sort_order: swapSortOrder })
          .eq('id', swapCategoryId),
      ]);

      if (result1.error) throw result1.error;
      if (result2.error) throw result2.error;
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });

      // Snapshot current data
      const previousCategories = queryClient.getQueryData<PropertyKitCategory[]>(QUERY_KEY);

      // Optimistically update the cache
      queryClient.setQueryData<PropertyKitCategory[]>(QUERY_KEY, (old) => {
        if (!old) return old;
        
        const updated = [...old];
        const idx1 = updated.findIndex(c => c.id === variables.categoryId);
        const idx2 = updated.findIndex(c => c.id === variables.swapCategoryId);
        
        if (idx1 !== -1 && idx2 !== -1) {
          // Swap positions in array
          [updated[idx1], updated[idx2]] = [updated[idx2], updated[idx1]];
          // Update sort_order values
          updated[idx1] = { ...updated[idx1], sort_order: variables.swapSortOrder };
          updated[idx2] = { ...updated[idx2], sort_order: variables.newSortOrder };
        }
        
        return updated;
      });

      return { previousCategories };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousCategories) {
        queryClient.setQueryData(QUERY_KEY, context.previousCategories);
      }
      toast({
        title: 'Error',
        description: 'No se pudo reordenar la categoría',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      // Sync with server
      queryClient.invalidateQueries({ 
        queryKey: QUERY_KEY,
        refetchType: 'all' 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['property-kit-items'],
        refetchType: 'all' 
      });
      queryClient.invalidateQueries({ 
        queryKey: ['property-articles'],
        refetchType: 'all' 
      });
    },
  });

  return {
    categories: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    createCategory: createMutation.mutateAsync,
    updateCategory: updateMutation.mutateAsync,
    deleteCategory: deleteMutation.mutateAsync,
    reorderCategory: reorderMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isReordering: reorderMutation.isPending,
  };
}
